/* eslint-disable no-unused-expressions */
import clsx from 'clsx';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { useRive } from 'rive-react';

import Link from 'components/shared/link';

const itemLeftClassName = 'pl-[104px] xl:pl-20 lg:pl-9';
const itemRightClassName = 'pr-[104px] xl:pr-20 lg:pr-9';

const riveAnimations = {
  'customer-center-customization': '/animations/customer-center/custom-interface.riv',
  'customer-center-feedback': '/animations/customer-center/collect-feedback.riv',
  'customer-center-offers': '/animations/customer-center/promotional-offer.riv',
  'customer-center-support': '/animations/customer-center/automate-support.riv',
  'home-paywalls': '/animations/home/paywalls.riv',
  'home-targeting': '/animations/home/targeting.riv',
  'home-testing': '/animations/home/ab-testing.riv',
  'streaming-growth': '/animations/streaming/cross-platform.riv',
  'streaming-prediction': '/animations/streaming/confidence.riv',
  'streaming-retention': '/animations/streaming/events.riv',
};

const SectionWithAnimatedSteps = ({ isLightBackground, title, description, items }) => {
  const [sectionRef, isSectionInView] = useInView({ triggerOnce: true });
  const [visibleAnimationName, setVisibleAnimationName] = useState([]);

  const {
    RiveComponent: CustomerCenterCustomizationAnimation,
    rive: customerCenterCustomizationRive,
  } = useRive({
    src: riveAnimations['customer-center-customization'],
    stateMachines: 'SM',
    autoplay: false,
  });
  const { RiveComponent: CustomerCenterFeedbackAnimation, rive: customerCenterFeedbackRive } =
    useRive({
      src: riveAnimations['customer-center-feedback'],
      stateMachines: 'SM',
      autoplay: false,
    });
  const { RiveComponent: CustomerCenterOffersAnimation, rive: customerCenterOffersRive } = useRive({
    src: riveAnimations['customer-center-offers'],
    stateMachines: 'SM',
    autoplay: false,
  });
  const { RiveComponent: CustomerCenterSupportAnimation, rive: customerCenterSupportRive } =
    useRive({
      src: riveAnimations['customer-center-support'],
      stateMachines: 'SM',
      autoplay: false,
    });
  const { RiveComponent: HomePaywallsAnimation, rive: homePaywallsRive } = useRive({
    src: riveAnimations['home-paywalls'],
    stateMachines: 'SM',
    autoplay: false,
  });
  const { RiveComponent: HomeTargetingAnimation, rive: homeTargetingRive } = useRive({
    src: riveAnimations['home-targeting'],
    stateMachines: 'SM',
    autoplay: false,
  });
  const { RiveComponent: HomeTestingAnimation, rive: homeTestingRive } = useRive({
    src: riveAnimations['home-testing'],
    stateMachines: 'SM',
    autoplay: false,
  });
  const { RiveComponent: StreamingGrowthAnimation, rive: streamingGrowthRive } = useRive({
    src: riveAnimations['streaming-growth'],
    stateMachines: 'SM',
    autoplay: false,
  });
  const { RiveComponent: StreamingPredictionAnimation, rive: streamingPredictionRive } = useRive({
    src: riveAnimations['streaming-prediction'],
    stateMachines: 'SM',
    autoplay: false,
  });
  const { RiveComponent: StreamingRetentionAnimation, rive: streamingRetentionRive } = useRive({
    src: riveAnimations['streaming-retention'],
    stateMachines: 'SM',
    autoplay: false,
  });

  const animationsAssets = {
    'customer-center-customization': {
      image: (
        <CustomerCenterCustomizationAnimation className="absolute -top-4 -left-[50px] w-[545px] pointer-events-none aspect-square xl:-left-10 xl:w-[430px] lg:-top-3 lg:-left-8 lg:w-[330px] md:top-2.5 md:-left-9 md:w-[368px]" />
      ),
      imageWrapperClassName: 'h-[500px] xl:h-[395px] lg:h-[300px] md:h-[377px] md:max-w-80',
    },
    'customer-center-feedback': {
      image: (
        <CustomerCenterFeedbackAnimation className="absolute -top-5 -left-[50px] w-[552px] pointer-events-none aspect-square xl:-top-5 xl:-left-10 xl:w-[433px] lg:-top-2 lg:-left-8 lg:w-[330px] md:top-2 md:-left-9 md:w-[368px]" />
      ),
      imageWrapperClassName: 'h-[500px] xl:h-[395px] lg:h-[300px] md:h-[377px] md:max-w-80',
    },
    'customer-center-offers': {
      image: (
        <CustomerCenterOffersAnimation className="absolute -top-6 -right-10 w-[546px] pointer-events-none aspect-[488/500] xl:-top-5 xl:-right-7 xl:w-[433px] lg:-top-3.5 lg:-right-6 lg:w-[330px] md:top-2 md:-right-7 md:w-[371px]" />
      ),
      imageWrapperClassName: 'h-[500px] xl:h-[395px] lg:h-[300px] md:h-[377px] md:max-w-80',
    },
    'customer-center-support': {
      image: (
        <CustomerCenterSupportAnimation className="absolute -top-6 -right-10 w-[546px] pointer-events-none aspect-[488/500] xl:-top-5 xl:-right-7 xl:w-[433px] lg:-top-3 lg:-right-6 lg:w-[330px] md:top-1.5 md:-right-7 md:w-[371px]" />
      ),
      imageWrapperClassName: 'h-[500px] xl:h-[395px] lg:h-[300px] md:h-[377px] md:max-w-80',
    },
    'home-paywalls': {
      image: (
        <>
          <span className="absolute rounded-full w-[509px] h-[264px] blur-[90px] rotate-[32.123deg] bg-secondary-2 opacity-[0.12] top-[45%] left-[10%] lg:w-[382px] lg:h-50" />
          <span className="absolute rounded-full w-[444px] h-[273px] blur-2xl rotate-[31.554deg] bg-[#F25A5A] opacity-[0.05] top-[7%] left-[18%] 2xl:left-[8%] xl:w-[350px] lg:w-[300px] lg:h-[204px] lg:left-[7%]" />
          <HomePaywallsAnimation className="relative -top-2 right-1 h-[500px] w-[488px] pointer-events-none xl:top-11 xl:right-4 xl:h-[474px] xl:w-[456px] xl:-mt-[82px] lg:right-2 lg:h-[374px] lg:w-[367px] lg:-mt-16 md:h-[296px] md:max-w-[388px] md:w-full md:top-0 md:right-0 md:mt-2" />
        </>
      ),
      imageWrapperClassName: '',
    },
    'home-targeting': {
      image: (
        <>
          <span className="absolute rounded-full w-[471px] h-[236px] blur-[90px] rotate-[54.276deg] bg-secondary-2 opacity-[0.12] top-[30%] left-[-24%] lg:w-[309px] lg:h-[155px] lg:left-0" />
          <span className="absolute rounded-full w-[434px] h-[281px] blur-2xl rotate-[50deg] bg-[#F25A5A] opacity-[0.05] top-[3%] left-[21%] lg:w-[284px] lg:h-[184px] lg:left-[35%] md:left-[20%]" />
          <HomeTargetingAnimation className="relative top-2 -left-2 h-[524px] w-[524px] pointer-events-none xl:top-0 xl:-left-3 xl:h-[433px] xl:w-[427px] xl:mt-2 lg:h-[327px] lg:w-[340px] lg:mt-10 md:h-[345px] md:max-w-[340px] md:w-full md:top-0 md:left-0 md:mt-2" />
        </>
      ),
      imageWrapperClassName: '',
    },
    'home-testing': {
      image: (
        <>
          <span className="absolute rounded-full w-[509px] h-[264px] blur-[90px] rotate-[32.123deg] bg-secondary-2 opacity-[0.12] top-[13%] left-[8%] xl:-top-[10%] lg:top-[22%] lg:w-[382px] lg:h-30" />
          <span className="absolute rounded-full w-[444px] h-[273px] blur-2xl rotate-[31.554deg] bg-[#F25A5A] opacity-[0.05] top-0 left-[35%] 2xl:left-[8%] xl:w-[350px] lg:w-[300px] lg:h-[204px] lg:left-[7%]" />
          <HomeTestingAnimation className="relative -top-[58px] right-5 h-[609px] w-[560px] -mb-44 pointer-events-none xl:-top-[74px] xl:right-[26px] xl:h-[444px] xl:w-[465px] xl:-mb-[150px] lg:-top-[52px] lg:right-4 lg:h-[363px] lg:w-[376px] lg:-mb-[120px] md:max-w-[344px] md:w-full md:h-[335px] md:mt-7 md:top-0 md:left-0 md:-mb-20" />
        </>
      ),
      imageWrapperClassName: '',
    },
    'streaming-growth': {
      image: (
        <StreamingGrowthAnimation className="w-[564px] h-[378px] my-[75px] rounded-3xl lg:rounded-[14px] shadow-[0px_8px_120px_0px_#F2545B1A,0px_0px_2px_0px_#17204340] overflow-hidden xl:w-[446px] xl:h-[298px] xl:my-[100px] lg:w-[336px] lg:h-56 lg:my-[72px] lg:pointer-events-none md:w-4/5 md:h-auto md:aspect-[1.5] sm:w-[90%] xs:flex xs:items-center xs:my-0" />
      ),
      imageWrapperClassName: 'flex xs:mt-14 xs:mb-[50px]',
    },
    'streaming-prediction': {
      image: (
        <StreamingPredictionAnimation className="w-[564px] h-[378px] my-[75px] rounded-2xl lg:rounded-[10px] shadow-[0px_8px_120px_0px_#576CDB1A,0px_0px_2px_0px_#17204340] overflow-hidden xl:w-[446px] xl:h-[298px] xl:my-[100px] lg:w-[336px] lg:h-56 lg:my-[72px] lg:pointer-events-none md:w-4/5 md:h-auto md:aspect-[1.5] sm:w-[90%] xs:flex xs:items-center xs:my-0" />
      ),
      imageWrapperClassName: 'flex xs:mt-14 xs:mb-[50px]',
    },
    'streaming-retention': {
      image: (
        <StreamingRetentionAnimation className="w-[564px] h-[378px] my-[75px] rounded-2xl lg:rounded-[10px] shadow-[0px_8px_120px_0px_#6781FF1A,0px_0px_2px_0px_#17204340] overflow-hidden xl:w-[446px] xl:h-[298px] xl:my-[100px] lg:w-[336px] lg:h-56 lg:my-[72px] lg:pointer-events-none md:w-4/5 md:h-auto md:aspect-[1.5] sm:w-[90%] xs:flex xs:items-center xs:my-0" />
      ),
      imageWrapperClassName: 'flex xs:mt-14 xs:mb-[50px]',
    },
  };

  const onItemViewChange = (inView, animationName) => {
    inView &&
      setVisibleAnimationName((prevState) => {
        const updatedAnimations = [...prevState];

        if (!updatedAnimations.includes(animationName)) {
          updatedAnimations.push(animationName);
        }

        return updatedAnimations;
      });
  };

  useEffect(() => {
    customerCenterCustomizationRive &&
      visibleAnimationName.includes('customer-center-customization') &&
      customerCenterCustomizationRive.play('SM');
    customerCenterFeedbackRive &&
      visibleAnimationName.includes('customer-center-feedback') &&
      customerCenterFeedbackRive.play('SM');
    customerCenterOffersRive &&
      visibleAnimationName.includes('customer-center-offers') &&
      customerCenterOffersRive.play('SM');
    customerCenterSupportRive &&
      visibleAnimationName.includes('customer-center-support') &&
      customerCenterSupportRive.play('SM');
    homePaywallsRive &&
      visibleAnimationName.includes('home-paywalls') &&
      homePaywallsRive.play('SM');
    homeTargetingRive &&
      visibleAnimationName.includes('home-targeting') &&
      homeTargetingRive.play('SM');
    homeTestingRive && visibleAnimationName.includes('home-testing') && homeTestingRive.play('SM');
    streamingGrowthRive &&
      visibleAnimationName.includes('streaming-growth') &&
      streamingGrowthRive.play('SM');
    streamingPredictionRive &&
      visibleAnimationName.includes('streaming-prediction') &&
      streamingPredictionRive.play('SM');
    streamingRetentionRive &&
      visibleAnimationName.includes('streaming-retention') &&
      streamingRetentionRive.play('SM');
  }, [
    visibleAnimationName,
    customerCenterCustomizationRive,
    customerCenterFeedbackRive,
    customerCenterOffersRive,
    customerCenterSupportRive,
    homePaywallsRive,
    homeTargetingRive,
    homeTestingRive,
    streamingGrowthRive,
    streamingPredictionRive,
    streamingRetentionRive,
  ]);

  return (
    <section
      className={clsx(
        'section-with-animated-steps safe-paddings py-40 xl:py-[120px] lg:py-28 md:py-20 xl:overflow-hidden',
        !isLightBackground && 'bg-new-gray-2'
      )}
      ref={sectionRef}
    >
      <div className="container grid grid-cols-12 gap-x-8 md:grid-cols-4">
        <h2 className="fs-56 col-start-2 col-span-10 px-30 text-center text-new-black lg:px-20 md:col-start-1 md:col-span-full md:max-w-80 md:mx-auto md:px-0">
          {title}
        </h2>
        {description && (
          <p className="fs-20 col-start-2 col-span-10 mt-5 px-44 text-center text-new-gray-1 xl:mt-4 xl:px-20 xl:text-[20px] lg:mt-3.5 lg:px-11 lg:text-[18px] md:col-start-1 md:col-span-full md:max-w-80 md:mx-auto md:mt-3 md:px-0 md:text-[16px]">
            {description}
          </p>
        )}
        <ul className="relative col-span-full grid gap-y-5 mt-11 before:absolute before:top-6 before:left-1/2 before:h-[calc(100%-80px)] before:w-px before:-translate-x-px before:bg-divider before:bg-[length:1px_4px,1px_100%] xl:gap-y-[57px] xl:mt-[50px] lg:gap-y-11 lg:mt-3 md:gap-y-10 md:max-w-md md:mt-11 md:mx-auto md:before:hidden">
          {items.map(({ illustrationName, title, description, linkText, linkUrl }, index) => {
            // eslint-disable-next-line
            const [ref] = useInView({
              triggerOnce: true,
              rootMargin: '100px 0px',
              onChange: (inView) => onItemViewChange(inView, illustrationName),
            });
            const { image, imageWrapperClassName } = animationsAssets[illustrationName];

            return (
              <li
                className={clsx(
                  'relative flex items-center gap-x-8 md:flex-col md:pt-[51px] md:before:absolute md:before:top-0 md:before:left-1/2 md:before:h-16 md:before:w-0.5 md:before:-translate-x-px md:before:bg-line-gradient',
                  index % 2 !== 0 && 'flex-row-reverse'
                )}
                ref={ref}
                key={index}
              >
                <div className="absolute left-1/2 size-8 -translate-x-4 flex items-center justify-center rounded-full bg-white shadow-[0_2px_6px_0_#0F1B571A] backdrop-blur-sm md:relative md:left-0 md:translate-x-0 md:size-6 md:mx-auto">
                  <span className="fs-16 mt-1 font-medium leading-none">{index + 1}</span>
                </div>
                <div
                  className={clsx(
                    'w-1/2 md:w-full md:px-0 md:pt-[18px] md:flex md:flex-col md:items-center',
                    index % 2 === 0 ? itemRightClassName : itemLeftClassName
                  )}
                >
                  <h3 className="fs-36 pr-6 leading-snug text-new-black xl:leading-denser md:px-6 md:text-center">
                    {title}
                  </h3>
                  <p className="fs-20 mt-3 text-new-gray-1 md:mt-2 md:px-2 md:text-center">
                    {description}
                  </p>
                  {linkText && linkUrl && (
                    <Link
                      className="mt-7 xl:mt-[18px] xl:text-[16px] lg:text-[13px] md:mt-3 md:text-[14px] xs:text-[13px]"
                      to={linkUrl}
                      theme="secondary-2"
                      size="md"
                      withArrow
                    >
                      {linkText}
                    </Link>
                  )}
                </div>
                <div
                  className={clsx(
                    'relative w-1/2 md:w-full md:px-0 md:justify-center',
                    imageWrapperClassName,
                    !illustrationName.startsWith('streaming') &&
                      (index % 2 === 0 ? itemLeftClassName : itemRightClassName),
                    illustrationName.startsWith('streaming') && index % 2 === 0 && 'justify-end'
                  )}
                >
                  {isSectionInView ? image : null}
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
};

SectionWithAnimatedSteps.propTypes = {
  isLightBackground: PropTypes.number,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      illustrationName: PropTypes.oneOf(Object.keys(riveAnimations)).isRequired,
      title: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
      linkText: PropTypes.string,
      linkUrl: PropTypes.string,
    })
  ).isRequired,
};

SectionWithAnimatedSteps.defaultProps = {
  isLightBackground: 0,
  description: null,
};

export default SectionWithAnimatedSteps;
